import {
  Heading,
  Text,
  HStack,
  VStack,
  Skeleton,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState } from 'react';

import { Bank } from '@finley-technologies/finley-types';
import {
  generateReceivableStats,
  getDedupedTotalMarketValue,
  parse,
  orderLoanAccountsLoans,
  getAllocatedTotalLendableValue,
} from '@finley-technologies/valley-bank-utils';
import type {
  ValleyBankCustomerWithRelationships,
  ValleyBankLoanAccount,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankCustomerQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import {
  getUnexpectedErrorMessage,
  formatCurrencyAmount,
} from 'packages/common-frontend/utils';
import { Alert } from '~/packages/common-frontend/components/Alert';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { InfoCard } from '~/valleyBank/domains/components/InfoCard';
import { useCustomerUrlParam } from '~/valleyBank/domains/Customers/hooks/CustomerContext';
import { CustomerNotFoundAlert } from '~/valleyBank/domains/Customers/ui/details/CustomerNotFoundAlert';
import { CollateralAccountsTable } from '~/valleyBank/domains/Customers/ui/details/SummaryOverviewStatus/CollateralAccountsTable';
import { LoansTable } from '~/valleyBank/domains/LoanAccounts/ui/LoansTable';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';
import { LoanAndCollateralAccountsStats } from '~/valleyBank/domains/page/LoanAccountsAndCollateralAccountsStats';
import { BoundingBox } from '~/valleyBank/domains/page/ui/BoundingBox';

export const SummaryOverviewStatus = () => {
  const toast = useToast();
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const [
    customer,
    setCustomer,
  ] = useState<ValleyBankCustomerWithRelationships | null>(null);

  // @jendevelops TODO: replace with loanParties props
  const { cifnum } = useCustomerUrlParam();
  const { loading: isLoading, error } = useGetValleyBankCustomerQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      cifNumber: cifnum || '',
      reportingDate,
      isPreview,
    },
    onCompleted: (res) => {
      const customerJSON = res.valleyBankCustomer?.customer;
      if (customerJSON) {
        try {
          const newCustomer = parse.parseValleyBankCustomer(customerJSON);
          if (newCustomer) {
            setAsOfDate(newCustomer.asOfDate);
            setCustomer({ ...newCustomer });
          }
        } catch (err) {
          throw new Error('Failed to parse customer');
        }
      }
    },
  });

  if (isLoading) {
    return <Skeleton w="100%" h="600px" />;
  }

  const loanAccountLoanPartyMap = customer?.loanAccounts.reduce(
    (acc, loanAccount) => {
      return {
        ...acc,
        [loanAccount.loanAccountNumber]: _.keyBy(
          loanAccount.loanParties,
          'cif_num',
        ),
      };
    },
    {} as {
      [loanAccountNumber: string]: {
        [cifNumber: string]: ValleyBankLoanAccount['loanParties'][number];
      };
    },
  );

  const loans = orderLoanAccountsLoans(customer?.loanAccounts || []);

  const collateralAccounts = customer?.collateralAccounts || [];

  const receivables =
    customer?.collateralAccounts.flatMap(
      ({ receivables: _receivables }) => _receivables,
    ) || [];

  const [assignedReceivables, unassignedReceivables] = _.partition(
    receivables,
    (receivable) => Boolean(receivable.loan_account_number),
  );

  const {
    totalPreAllocationLendableValue: unassignedTotalPreAllocationLendableValue,
    totalMarketValue: unassignedTotalMarketValue,
  } = generateReceivableStats(unassignedReceivables);

  return (
    <>
      {!isLoading && !customer && <CustomerNotFoundAlert />}

      {customer && (
        <VStack w="100%" spacing="24px" alignItems="flex-start">
          <LoanAndCollateralAccountsStats
            collateralStats={{
              assigned: generateReceivableStats(assignedReceivables),
              unassigned: generateReceivableStats(unassignedReceivables),
            }}
          />

          <BoundingBox>
            <LoansTable
              loans={loans}
              noLoansMessage="There are no loan associated with this customer."
              title="Loans"
              loanAccountLoanPartyMap={loanAccountLoanPartyMap}
              cifNumber={cifnum}
              isLoading={isLoading}
              isError={Boolean(error)}
              downloadFilenamePrefix={`loans-customer-${cifnum}`}
              pageType="customers"
            />
          </BoundingBox>

          <BoundingBox>
            <Heading as="h2" fontSize="24px" mb="24px">
              Collateral Accounts
            </Heading>

            {collateralAccounts.length === 0 && (
              <Alert status="info">
                <Text>
                  No Assigned Collateral associated with this Customer
                </Text>
              </Alert>
            )}
            {collateralAccounts.length > 0 && (
              <>
                <HStack
                  spacing="24px"
                  w="100%"
                  style={{ marginBottom: '24px' }}
                >
                  <InfoCard
                    title="Total Assigned Market Value"
                    value={formatCurrencyAmount(
                      getDedupedTotalMarketValue(assignedReceivables),
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                  <InfoCard
                    title="Total Assigned Lendable Value"
                    value={formatCurrencyAmount(
                      getAllocatedTotalLendableValue(assignedReceivables),
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                  <InfoCard
                    title="Total Unassigned Market Value"
                    value={formatCurrencyAmount(
                      unassignedTotalMarketValue,
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                  <InfoCard
                    title="Total Unassigned Lendable Value"
                    value={formatCurrencyAmount(
                      unassignedTotalPreAllocationLendableValue,
                      Bank.Currency.USD,
                    )}
                    icon={<Icon icon="dollar" size="24px" />}
                    flexGrow={1}
                  />
                </HStack>
                {/* @jendevelops TODO implement search and show/hide columns */}
                {/* <HStack w="100%" spacing="16px" justifyContent="flex-end">
              <Button variant="outline" colorScheme="aurora">
                Show/Hide Columns
              </Button>
              <Button
                variant="outline"
                colorScheme="aurora"
                leftIcon={<Icon icon="search" />}
              >
                Search
              </Button>
            </HStack> */}
                <CollateralAccountsTable
                  collateralAccounts={collateralAccounts}
                  cifNum={cifnum}
                  isLoading={isLoading}
                  isError={Boolean(error)}
                />
              </>
            )}
          </BoundingBox>
        </VStack>
      )}
    </>
  );
};
